// Namespaced storage wrapper to prevent future conflicts
const STORAGE_PREFIX = 'tilted_v1_';

// App version - increment this when making breaking changes
export const APP_VERSION = '2.1.0';

// Helper to save and restore auth data during cache clear
const saveAuthData = () => {
  const authKeys = Object.keys(localStorage).filter(k => 
    k.includes('supabase') || 
    k.includes('auth') || 
    k.includes('tilted-auth') ||
    k.includes('sb-')
  );
  const authData = {};
  authKeys.forEach(k => authData[k] = localStorage.getItem(k));
  return authData;
};

const restoreAuthData = (authData) => {
  Object.entries(authData).forEach(([k, v]) => {
    if (v !== null) localStorage.setItem(k, v);
  });
};

// Check and clear old cache on version change
export const checkAndClearOldCache = () => {
  try {
    const storedVersion = localStorage.getItem('tilted_app_version');
    
    if (storedVersion !== APP_VERSION) {
      console.log(`App version changed (${storedVersion || 'none'} → ${APP_VERSION}), clearing old cache...`);
      
      // Save auth-related keys before clearing
      const authData = saveAuthData();
      
      // Clear everything
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore auth so user stays logged in
      restoreAuthData(authData);
      
      // Set new version
      localStorage.setItem('tilted_app_version', APP_VERSION);
      
      console.log('Cache cleared successfully');
      
      // Only reload if this isn't the first load
      if (storedVersion) {
        console.log('Reloading app with fresh cache...');
        window.location.reload();
        return true; // Indicate we're reloading
      }
    }
    return false;
  } catch (e) {
    console.error('Error checking cache version:', e);
    return false;
  }
};

// Manual cache clear function (keeps user logged in)
export const clearAppCache = () => {
  try {
    console.log('Manually clearing app cache...');
    
    // Save auth-related keys
    const authData = saveAuthData();
    
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Restore auth
    restoreAuthData(authData);
    
    // Set current version
    localStorage.setItem('tilted_app_version', APP_VERSION);
    
    console.log('Cache cleared, reloading...');
    window.location.reload();
  } catch (e) {
    console.error('Error clearing cache:', e);
    // Force full clear and reload
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  }
};

// Namespaced storage wrapper
export const storage = {
  set: (key, value) => {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    } catch (e) {
      console.error('Storage set error:', e);
    }
  },
  
  get: (key) => {
    try {
      const item = localStorage.getItem(STORAGE_PREFIX + key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      console.error('Storage get error:', e);
      return null;
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch (e) {
      console.error('Storage remove error:', e);
    }
  },
  
  clear: () => {
    // Only clear our app's namespaced keys
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(STORAGE_PREFIX))
        .forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.error('Storage clear error:', e);
    }
  }
};

export default storage;
